import React, { useEffect } from "react";
import CaseStudyLayout from "../CaseStudyLayout";
import ImageGallery from "../ImageGallery";

const BxKicks = () => {
  useEffect(() => {
    document.title = "BX Kicks E-commerce Case Study - Andy Persaud Portfolio";
    window.scrollTo(0, 0);
  }, []);

  return (
    <CaseStudyLayout>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-purple-50 to-indigo-50 section-padding">
        <div className="container-narrow">
          <div className="text-center mb-12">
            <div className="mb-8">
              <img
                src="https://customer-assets.emergentagent.com/job_uxdev-portfolio/artifacts/wltnflho_Screenshot%202025-11-14%20at%208.53.32%E2%80%AFPM.png"
                alt="BX Kicks Website Homepage - Your Bronx Plug for Real Sneakers"
                className="w-full max-w-4xl mx-auto rounded-2xl shadow-xl animate-fade-in"
              />
            </div>

            <h1 className="heading-hero animate-slide-up text-gray-900 mb-4">BX Kicks</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed animate-slide-up">
              Premium sneaker e-commerce platform built for the Bronx streetwear community.
            </p>
          </div>

          {/* Project Overview */}
          <div className="grid md:grid-cols-4 gap-8 card-professional p-8 animate-scale-in">
            <div className="text-center">
              <h3 className="font-semibold text-gray-900 mb-2">Role</h3>
              <p className="text-gray-600">Lead UX/UI Designer &amp; Developer</p>
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-gray-900 mb-2">Platform</h3>
              <p className="text-gray-600">Custom Shopify E-commerce</p>
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-gray-900 mb-2">Timeline</h3>
              <p className="text-gray-600">3 Months</p>
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-gray-900 mb-2">Engagement</h3>
              <p className="text-gray-600">0 → 1 Strategy, UX, Build &amp; Launch</p>
            </div>
          </div>

          {/* Visit Live Website CTA */}
          <div className="text-center mt-8">
            <a
              href="https://bxkicks.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="interactive-button inline-flex items-center space-x-2 animate-fade-in"
            >
              <span>Visit Live Website</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </a>
          </div>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="section-padding bg-white">
        <div className="container-narrow">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div className="metric-card">
              <div className="metric-number text-purple-600">100%</div>
              <div className="metric-label">Mobile Optimized</div>
              <div className="metric-description">Designed mobile-first for 70%+ mobile users.</div>
            </div>
            <div className="metric-card">
              <div className="metric-number text-green-600">0 → 100%</div>
              <div className="metric-label">Online Presence</div>
              <div className="metric-description">From no site to a full e-commerce platform.</div>
            </div>
            <div className="metric-card">
              <div className="metric-number text-blue-600">90%</div>
              <div className="metric-label">Fulfillment Time Savings</div>
              <div className="metric-description">Automated labels cut per-order time from 10+ to &lt; 2 minutes.</div>
            </div>
          </div>
        </div>
      </section>

      {/* Challenge Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-narrow">
          <h2 className="heading-section text-center mb-8">The Challenge</h2>
          <div className="card-professional bg-white p-8 border-l-4 border-purple-500">
            <div className="flex items-start space-x-4">
              <div className="text-4xl">🏪</div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  From In-Person Meetups to a Trustworthy Online Storefront
                </h3>
                <p className="text-body mb-6">
                  BX Kicks needed to evolve from in-person, DM-based sales into a professional online storefront
                  that could stand on its own. Customers had to feel confident buying high-value sneakers from a
                  brand they previously only knew through Instagram and word of mouth.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="font-semibold text-red-800 mb-2">Business Challenges</h4>
                    <ul className="text-red-700 space-y-1 text-sm">
                      <li>• No professional, self-serve storefront.</li>
                      <li>• Sales dependent on manual DMs and meetups.</li>
                      <li>• No scalable inventory or order system.</li>
                      <li>• Time-consuming manual shipping label creation.</li>
                    </ul>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-2">User Pain Points</h4>
                    <ul className="text-blue-700 space-y-1 text-sm">
                      <li>• Multi-step checkout with external redirects causing confusion.</li>
                      <li>• No visibility into shipping costs until late in checkout.</li>
                      <li>• Out-of-stock items still visible, creating disappointment.</li>
                      <li>• No easy way to track orders or contact the business.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* UX Goals & Success Criteria */}
      <section className="section-padding bg-white">
        <div className="container-narrow space-y-8">
          <h2 className="heading-section text-center mb-4">UX Goals &amp; Success Criteria</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card-professional p-6 bg-gray-50 border border-purple-100">
              <h3 className="heading-subsection mb-4">Primary UX Goals</h3>
              <ul className="text-gray-700 space-y-2 text-sm">
                <li>• Reduce checkout friction with a single-page flow and no external redirects.</li>
                <li>• Build trust and transparency with clear pricing, shipping, and delivery times.</li>
                <li>• Streamline fulfillment with automated workflows for the founder.</li>
                <li>• Ensure accessibility and usability on mobile and desktop.</li>
              </ul>
            </div>
            <div className="card-professional p-6 bg-gray-50 border border-green-100">
              <h3 className="heading-subsection mb-4">Success Metrics</h3>
              <ul className="text-gray-700 space-y-2 text-sm">
                <li>• Increased checkout completion rate vs. the original flow.</li>
                <li>• Reduced time-to-purchase from landing to confirmation.</li>
                <li>• Zero overselling due to tighter inventory controls.</li>
                <li>• Significant admin time savings per order.</li>
              </ul>
            </div>
          </div>

          {/* Checkout Flow Before & After */}
          <div className="card-professional p-6 bg-gray-50 border border-slate-200 space-y-6">
            <h3 className="heading-subsection text-center">
              Checkout Flow Redesign &mdash; Before vs After
            </h3>
            <p className="text-body text-center max-w-3xl mx-auto">
              Visualizing the shift from a multi-page, redirect-heavy checkout to a single, streamlined flow with
              inline payments and a persistent order summary.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <figure className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
                <div className="px-4 pt-4 pb-2 flex items-center justify-between">
                  <span className="text-xs font-semibold tracking-wide text-red-700 uppercase">Before</span>
                  <span className="text-[11px] font-medium text-red-600 bg-red-50 px-3 py-1 rounded-full">
                    Multi-Page Flow
                  </span>
                </div>
                <img
                  src="https://customer-assets.emergentagent.com/job_ux-design-revamp/artifacts/2qfj7mer_checkout%20before.jpeg"
                  alt="Original BX Kicks checkout flow with multiple redirect steps"
                  className="w-full h-auto object-contain bg-slate-50"
                />
              </figure>

              <figure className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
                <div className="px-4 pt-4 pb-2 flex items-center justify-between">
                  <span className="text-xs font-semibold tracking-wide text-emerald-700 uppercase">After</span>
                  <span className="text-[11px] font-medium text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full">
                    Single-Page Flow
                  </span>
                </div>
                <img
                  src="https://customer-assets.emergentagent.com/job_ux-design-revamp/artifacts/uq4mq0fm_checkout%20after.jpeg"
                  alt="Redesigned BX Kicks checkout with single-page progressive steps and summary"
                  className="w-full h-auto object-contain bg-slate-50"
                />
              </figure>
            </div>
          </div>

          {/* Pain Points vs Benefits */}
          <div className="mt-6 grid md:grid-cols-2 gap-6">
            {/* Before: pain points */}
            <div className="rounded-3xl bg-red-50 border border-red-100 px-6 py-5 shadow-sm">
              <h3 className="text-base font-semibold text-red-800 mb-3">Before &mdash; Pain Points</h3>
              <ul className="list-disc list-inside text-sm text-red-800 space-y-1.5">
                <li>Multi-page checkout with external redirects created friction and confusion.</li>
                <li>Shipping costs surfaced late in the flow, causing price shock and abandonment.</li>
                <li>No persistent order summary made it hard to understand what was being purchased.</li>
                <li>Long perceived time-to-purchase with repeated page loads and form re-entry.</li>
              </ul>
            </div>

            {/* After: benefits */}
            <div className="rounded-3xl bg-blue-50 border border-blue-100 px-6 py-5 shadow-sm">
              <h3 className="text-base font-semibold text-blue-800 mb-3">After &mdash; Benefits</h3>
              <ul className="list-disc list-inside text-sm text-blue-800 space-y-1.5">
                <li>Single-page, progressive checkout keeps users oriented with clear steps.</li>
                <li>Inline payment and up-front shipping costs reduce surprises at the end of the flow.</li>
                <li>Persistent order summary builds trust and keeps totals visible at every step.</li>
                <li>Faster, more focused experience that supports both mobile and desktop buyers.</li>
              </ul>
            </div>
          </div>

          {/* Expected Impact from Redesign */}
          <div className="card-professional mt-6 p-6 bg-white border border-slate-200">
            <h3 className="heading-subsection text-center mb-6">Expected Impact</h3>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div className="space-y-2">
                <div className="text-3xl font-bold text-purple-600">-60%</div>
                <div className="text-sm font-semibold text-gray-900">Page Load Time</div>
                <div className="text-xs text-gray-500">From 5 pages to 1 consolidated flow.</div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl font-bold text-emerald-600">+25%</div>
                <div className="text-sm font-semibold text-gray-900">Conversion Rate</div>
                <div className="text-xs text-gray-500">Reduced friction and clearer checkout steps.</div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl font-bold text-blue-600">-40%</div>
                <div className="text-sm font-semibold text-gray-900">Cart Abandonment</div>
                <div className="text-xs text-gray-500">Streamlined single-page flow with visibility of costs.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Checkout & Fulfillment Experience */}
      <section className="section-padding bg-gray-50">
        <div className="container-narrow space-y-12">
          <h2 className="heading-section text-center">
            Designing a Frictionless Checkout &amp; Fulfillment Flow
          </h2>

          {/* Single-Page Checkout Redesign */}
          <div className="card-professional p-8 bg-white border border-purple-200 space-y-6">
            <h3 className="heading-subsection">1. Single-Page Checkout Redesign</h3>
            <p className="text-body">
              The original checkout required users to fill forms and then jump to an external Stripe page for
              payment. This created a disjointed experience, trust concerns, and unnecessary drop-off points.
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Progressive 4-Step Flow</h4>
                <ol className="list-decimal list-inside text-gray-700 text-sm space-y-2">
                  <li>
                    <strong>Contact:</strong> Email entry with inline validation and clear error messages.
                  </li>
                  <li>
                    <strong>Shipping Address:</strong> Structured form with field-level validation and required
                    indicators.
                  </li>
                  <li>
                    <strong>Shipping Method:</strong> Real-time carrier options with price and ETA comparison.
                  </li>
                  <li>
                    <strong>Payment:</strong> Inline card input (no redirect) with security messaging.
                  </li>
                </ol>

                <div className="mt-8 space-y-4">
                  <h4 className="font-semibold text-gray-900 text-center">
                    Single-Page Checkout in Action
                  </h4>
                  <p className="text-body text-center max-w-3xl mx-auto">
                    Mobile and desktop views of the four-step checkout flow and confirmation state.
                  </p>
                  <ImageGallery
                    columns={2}
                    images={[
                      {
                        src: "https://customer-assets.emergentagent.com/job_ux-design-revamp/artifacts/ufl6hqow_checkout%20mobile.jpeg",
                        alt: "BX Kicks mobile checkout — contact, address, and payment steps",
                        caption:
                          "Mobile-first, four-step checkout keeps the summary visible while guiding users through contact, address, shipping, and payment.",
                      },
                      {
                        src: "https://customer-assets.emergentagent.com/job_ux-design-revamp/artifacts/d16zm05h_checkout%201.jpeg",
                        alt: "BX Kicks desktop checkout step 1 — contact information with order summary",
                        caption:
                          "Step 1 — Contact information with an always-visible order summary panel on desktop.",
                      },
                      {
                        src: "https://customer-assets.emergentagent.com/job_ux-design-revamp/artifacts/38iqdkcx_checkout%202.jpeg",
                        alt: "BX Kicks desktop checkout step 2 — shipping address form",
                        caption:
                          "Step 2 — Structured shipping address form with clear field labels and validation.",
                      },
                      {
                        src: "https://customer-assets.emergentagent.com/job_ux-design-revamp/artifacts/6gc6vl38_Checkout%203.jpeg",
                        alt: "BX Kicks desktop checkout step 3 — shipping method options",
                        caption:
                          "Step 3 — Real-time shipping options with carrier, price, and delivery windows.",
                      },
                      {
                        src: "https://customer-assets.emergentagent.com/job_ux-design-revamp/artifacts/pig6scy4_checkout%204.jpeg",
                        alt: "BX Kicks desktop checkout step 4 — payment details and confirmation CTA",
                        caption:
                          "Step 4 — Payment details with card entry, totals, and a clear primary action.",
                      },
                      {
                        src: "https://customer-assets.emergentagent.com/job_ux-design-revamp/artifacts/fhdhuw44_order%20confirmation.jpeg",
                        alt: "BX Kicks order confirmation screen with items, totals, and shipping details",
                        caption:
                          "Order confirmation reinforces trust with a clear summary, delivery window, and next steps for the customer.",
                      },
                    ]}
                  />
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Key UX Decisions</h4>
                <ul className="text-gray-700 text-sm space-y-2">
                  <li>• Progress indicator shows where users are and what&apos;s left.</li>
                  <li>• Back navigation lets users edit without losing data.</li>
                  <li>• Persistent order summary keeps totals visible.</li>
                  <li>• &quot;Pay&quot; button disabled until all required fields are valid.</li>
                  <li>• Loading states for shipping rates and payment confirmation.</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Dynamic Shipping Cost Transparency */}
          <div className="card-professional p-8 bg-white border border-blue-200 space-y-6">
            <h3 className="heading-subsection">2. Dynamic Shipping Cost Transparency</h3>
            <p className="text-body">
              Previously, customers only saw shipping costs at the end of checkout, leading to sticker shock and
              abandonment. I integrated a real-time shipping rate API so shipping options appear immediately after
              the address is complete.
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">UX Decisions</h4>
                <ul className="text-gray-700 text-sm space-y-2">
                  <li>• Cheapest option pre-selected but full choice available.</li>
                  <li>• Carrier logo, service level, price, and ETA in a clear list.</li>
                  <li>• Order total updates in real time when options change.</li>
                  <li>• Selected method surfaced again in confirmation and emails.</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Impact</h4>
                <ul className="text-gray-700 text-sm space-y-2">
                  <li>• Clear expectations around cost and delivery windows.</li>
                  <li>• Reduced surprise fees at the end of checkout.</li>
                  <li>• Higher trust in the purchase experience.</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Inventory & Admin Workflows */}
          <div className="card-professional p-8 bg-white border border-emerald-200 space-y-6">
            <h3 className="heading-subsection">3. Intelligent Inventory &amp; Admin Workflows</h3>
            <p className="text-body">
              A major part of this project was designing an admin experience that prevents overselling and reduces
              manual work. Even though it sits behind the scenes, these UX decisions directly impact trust and
              fulfillment speed.
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Inventory Management</h4>
                <ul className="text-gray-700 text-sm space-y-2">
                  <li>• Auto-hide products with zero inventory from the storefront.</li>
                  <li>• Cart prevents adding more items than stock allows.</li>
                  <li>• Clear &quot;Out of Stock&quot; states on variants.</li>
                  <li>• Real-time inventory updates via purchase webhooks.</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Admin Workflow Optimization</h4>
                <ul className="text-gray-700 text-sm space-y-2">
                  <li>• Orders list with filters, status, and label actions.</li>
                  <li>• Auto shipping label generation on order completion.</li>
                  <li>• Tracking numbers surfaced with copy-to-clipboard actions.</li>
                  <li>• Simple controls for order lifecycle management.</li>
                </ul>
              </div>
            </div>
            <p className="text-sm text-emerald-900 mt-2">
              These decisions reduced fulfillment time per order from 10+ minutes of manual work to under 2 minutes.
            </p>
          </div>
        </div>
      </section>

      {/* Form Design, Content & Error Handling */}
      <section className="section-padding bg-white">
        <div className="container-narrow space-y-12">
          <h2 className="heading-section text-center">Form Design, Content Strategy &amp; Error Handling</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card-professional p-8 bg-gray-50 border border-slate-200 space-y-4">
              <h3 className="heading-subsection">Form Design &amp; Validation</h3>
              <p className="text-body">
                Forms appear across checkout, contact, admin login, and product creation. I applied consistent
                patterns to keep them usable and accessible.
              </p>
              <ul className="text-gray-700 text-sm space-y-2">
                <li>• Inline validation with clear, actionable errors.</li>
                <li>• Required fields marked visually and with asterisks.</li>
                <li>• Disabled submit buttons until forms are valid.</li>
                <li>• Field-level feedback on blur for email and address.</li>
                <li>• Proper labels and focus states for accessibility.</li>
              </ul>
            </div>
            <div className="card-professional p-8 bg-gray-50 border border-slate-200 space-y-4">
              <h3 className="heading-subsection">Content Strategy &amp; Messaging</h3>
              <p className="text-body">
                To differentiate BX Kicks from generic resale platforms, I leaned into the founder&apos;s Bronx story and
                community-driven positioning.
              </p>
              <ul className="text-gray-700 text-sm space-y-2">
                <li>• Homepage hero around &quot;Your Bronx Plug for Real Sneakers.&quot;</li>
                <li>• First-person About content centered on community and trust.</li>
                <li>• Friendly, human error messages instead of technical jargon.</li>
                <li>• Branded confirmation emails with clear next steps.</li>
              </ul>
            </div>
          </div>

          <div className="card-professional p-8 bg-gray-50 border border-red-200 space-y-4">
            <h3 className="heading-subsection text-red-800">Error Handling &amp; Recovery</h3>
            <p className="text-body">
              The experience is designed to prevent errors where possible, and when they do occur, give users a
              clear path to recover.
            </p>
            <ul className="text-gray-800 text-sm space-y-2">
              <li>• Cart stock limits with disabled controls and clear messaging.</li>
              <li>• Payment failures surfaced inline with retry options.</li>
              <li>• Address validation errors shown before final submission.</li>
              <li>• API failures fall back with non-blocking &quot;Try again&quot; actions.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Results & Learnings */}
      <section className="section-padding bg-gray-50">
        <div className="container-narrow space-y-12">
          <h2 className="heading-section text-center">Results, Impact &amp; Lessons</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card-professional p-8 bg-green-50 border border-green-200 space-y-3">
              <h3 className="heading-subsection text-green-800">Quantifiable Improvements</h3>
              <ul className="text-gray-800 text-sm space-y-2">
                <li>• Reduced checkout abandonment via a single-page, non-redirect flow.</li>
                <li>• Zero overselling due to inventory checks and out-of-stock handling.</li>
                <li>• ~90% time savings on fulfillment through label automation.</li>
                <li>• 100% mobile-responsive experience across devices.</li>
              </ul>
            </div>
            <div className="card-professional p-8 bg-blue-50 border border-blue-200 space-y-3">
              <h3 className="heading-subsection text-blue-800">Qualitative Improvements</h3>
              <ul className="text-gray-800 text-sm space-y-2">
                <li>• Professional brand presence vs. informal marketplace listings.</li>
                <li>• Clear pricing and shipping costs improved trust.</li>
                <li>• Self-service flows reduced support burden on the founder.</li>
                <li>• Site better reflects Bronx roots and community focus.</li>
              </ul>
            </div>
          </div>

          <div className="card-professional p-8 bg-white border border-purple-200 space-y-3">
            <h3 className="heading-subsection text-purple-800">Key Lessons</h3>
            <ul className="text-gray-800 text-sm space-y-2">
              <li>• Progressive disclosure in checkout reduces overwhelm and keeps users moving.</li>
              <li>• Showing shipping costs early is critical for building trust.</li>
              <li>• Automating admin tasks frees founders to focus on growth.</li>
              <li>• Testing edge cases (stock, payments, addresses) is essential for reliability.</li>
              <li>• Mobile-first design is non-negotiable for sneaker buyers.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="container-narrow">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <button
              type="button"
              onClick={() => window.history.back()}
              className="interactive-link flex items-center"
              data-testid="back-to-portfolio"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Portfolio
            </button>

            <div className="text-center">
              <div className="text-sm text-gray-500 mb-2">Case Study: BX Kicks E-commerce</div>
              <a
                href="https://bxkicks.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="interactive-button text-sm inline-flex items-center space-x-2"
              >
                <span>Experience Live Site</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>
    </CaseStudyLayout>
  );
};

export default BxKicks;
